export interface Rgb {
  r: number;
  g: number;
  b: number;
}

const HEX6_PATTERN = /^#([0-9A-Fa-f]{6})$/;
const HEX3_PATTERN = /^#([0-9A-Fa-f]{3})$/;

/**
 * Parse #RGB or #RRGGBB; returns null when invalid.
 */
export function parseFlexibleHex(input: string): string | null {
  const trimmed = input.trim();
  const six = trimmed.match(HEX6_PATTERN);
  if (six) {
    return `#${six[1]!.toUpperCase()}`;
  }
  const three = trimmed.match(HEX3_PATTERN);
  if (three) {
    const [r, g, b] = three[1]!.split('');
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  return null;
}

/**
 * Normalize to uppercase #RRGGBB or null.
 */
export function normalizeHex6(input: string): string | null {
  return parseFlexibleHex(input);
}

export function hexToRgb(hex: string): Rgb {
  const normalized = parseFlexibleHex(hex);
  if (!normalized) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  const value = normalized.slice(1);
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

/**
 * Relative luminance (WCAG) for a hex color.
 */
export function getRelativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const toLinear = (channel: number) => {
    const s = channel / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Whether hex is "light" for choosing black vs white foreground text.
 */
export function isHexLight(hex: string): boolean {
  return getRelativeLuminance(hex) > 0.55;
}

/**
 * WCAG contrast ratio between two hex colors (1–21).
 */
export function getContrastRatio(foregroundHex: string, backgroundHex: string): number {
  const l1 = getRelativeLuminance(foregroundHex);
  const l2 = getRelativeLuminance(backgroundHex);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Near-black / near-white text for maximum contrast on a painted surface. */
export function contrastingForegroundHex(backgroundHex: string): string {
  return isHexLight(backgroundHex) ? '#1f1f1f' : '#ffffff';
}

/** Secondary/muted text on a painted surface. */
export function mutedForegroundHex(backgroundHex: string): string {
  return isHexLight(backgroundHex) ? '#636363' : '#a8a8a8';
}

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

export function darkenHex(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const factor = 1 - amount;
  const nr = clampByte(r * factor);
  const ng = clampByte(g * factor);
  const nb = clampByte(b * factor);
  return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`.toUpperCase();
}

export function lightenHex(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const nr = clampByte(r + (255 - r) * amount);
  const ng = clampByte(g + (255 - g) * amount);
  const nb = clampByte(b + (255 - b) * amount);
  return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`.toUpperCase();
}
